import React from 'react';
import { BookMarked, Brain, ChevronRight, ExternalLink, Info, Scale, ShieldCheck } from 'lucide-react';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import {
  collectLexNormIds,
  extractArkoSources,
  extractLexQuotesFromText,
  hasArkoLexSources,
  type ArkoSourceItem,
} from './arkoSources';
import type { MemProofItem } from './memcore';
import {
  hasJudicatureProofData,
  stripJudicatureStampForDisplay,
  type JudicatureProof,
  type JudicatureProofCase,
} from './judicatureProof';
import {
  explainLexProof,
  firstRisUrlFromProof,
  formatBotMessageForDisplay as formatLexBubbleText,
  formatLexRetrievedAt,
  hasLexProofData,
  isLexRefusal,
  isVerifiedLexProof,
  type LexProof,
  type LexProofSource,
} from './lexProof';

/** Vertrauensstufe einer ARKO-Antwort fürs Chat-Badge (Stream 2). */
export type ArkoTrustLevel = 'verified' | 'orientation';

export interface ArkoTrust {
  level: ArkoTrustLevel;
  /** Verifizierte Norm-IDs (für die Badge-Beschriftung). */
  normIds: string[];
  /** Erste klickbare RIS-URL, falls vorhanden. */
  risUrl?: string;
  /** Roher LEX-Stempel aus dem Text (Fallback ohne strukturierten Nachweis). */
  lexStamp?: string;
}

/**
 * Stuft eine Bot-Antwort als „verifiziert" (echte LEXCORE-Norm) oder „allgemeine
 * Orientierung" ein. Baut additiv auf lexProof + Text-Parsing auf — keine neue
 * Datenquelle. Ehrliche Absagen zählen bewusst als Orientierung.
 */
export function classifyArkoTrust(text: string, lexProof?: LexProof | null): ArkoTrust {
  const normIds = collectLexNormIds(text, lexProof);
  const refusal = isLexRefusal(text);

  let risUrl = firstRisUrlFromProof(lexProof);
  let lexStamp: string | undefined;
  if (!risUrl || normIds.length === 0) {
    for (const item of extractArkoSources(text)) {
      if (!risUrl && item.kind === 'ris' && item.url.startsWith('http')) risUrl = item.url;
      if (!lexStamp && item.kind === 'lex_stamp') lexStamp = item.raw;
    }
  }

  const verified =
    !refusal && (isVerifiedLexProof(lexProof) || normIds.length > 0 || Boolean(lexStamp));

  return {
    level: verified ? 'verified' : 'orientation',
    normIds,
    risUrl,
    lexStamp,
  };
}

const RIS_TITLE = 'Quelle im Rechtsinformationssystem des Bundes (RIS) öffnen';

/**
 * Sichtbares Vertrauens-Badge über/neben der Antwort:
 * grün „verifiziert · [Norm] · RIS" vs. grau „allgemeine Orientierung".
 */
export const ArkoTrustBadge: React.FC<{ text: string; lexProof?: LexProof | null }> = ({
  text,
  lexProof,
}) => {
  const trust = classifyArkoTrust(text, lexProof);

  if (trust.level === 'verified') {
    const normLabel =
      trust.normIds.length === 1
        ? trust.normIds[0]
        : trust.normIds.length > 1
          ? `${trust.normIds.length} Normen`
          : null;
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200"
        title="Verifizierter Wortlaut aus dem LEXCORE-Snapshot — Quellen im Nachweis unten."
      >
        <ShieldCheck className="w-3 h-3" aria-hidden />
        verifiziert
        {normLabel ? (
          <span className="font-mono text-emerald-300/90">· {normLabel}</span>
        ) : null}
        {trust.risUrl ? (
          <a
            href={trust.risUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-emerald-300/90 underline decoration-emerald-400/40 underline-offset-2 hover:text-emerald-100 transition-colors"
            title={RIS_TITLE}
          >
            <ExternalLink className="w-3 h-3" aria-hidden />
            RIS
          </a>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-slate-400"
      title="Allgemeine Orientierung ohne verifizierte LEXCORE-Quelle — keine verbindliche Rechtsauskunft."
    >
      <Info className="w-3 h-3" aria-hidden />
      allgemeine Orientierung
    </span>
  );
};

function renderLexSourceItem(item: ArkoSourceItem, index: number) {
  if (item.kind === 'lex_stamp') {
    return (
      <PortalBadge
        key={`lex-${index}`}
        label={item.raw}
        accent="emerald"
        className="!px-2 !py-0.5 !text-[9px] !font-mono max-w-full truncate"
      />
    );
  }
  if (item.kind === 'norm') {
    return (
      <PortalBadge
        key={`norm-${item.normId}-${index}`}
        label={item.normId}
        accent="violet"
        className="!px-2 !py-0.5 !text-[9px] !font-mono"
      />
    );
  }
  if (item.kind === 'ris') {
    return (
      <a
        key={`ris-${index}`}
        href={item.url.startsWith('http') ? item.url : undefined}
        target="_blank"
        rel="noreferrer"
        className="block text-[10px] text-slate-400 hover:text-slate-200 transition-colors truncate"
        title={item.validFrom ? `Gültig ab ${item.validFrom}` : undefined}
      >
        RIS: {item.url}
        {item.validFrom ? ` · gültig ab ${item.validFrom}` : ''}
      </a>
    );
  }
  return null;
}

function LexProofSourceCard({ source }: { source: LexProofSource }) {
  const risHref = source.ris_url?.startsWith('http') ? source.ris_url : undefined;
  return (
    <div className="rounded border border-emerald-500/15 bg-emerald-500/[0.04] px-2 py-1.5 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <PortalBadge
          label={source.norm_id}
          accent="violet"
          className="!px-2 !py-0.5 !text-[9px] !font-mono"
        />
        {source.valid_from ? (
          <span className="text-[9px] text-slate-500">gültig ab {source.valid_from}</span>
        ) : null}
      </div>
      {source.title ? (
        <p className="text-[10px] font-medium text-slate-300 leading-snug">{source.title}</p>
      ) : null}
      {source.fragment_text ? (
        <p className="text-[10px] text-slate-400 leading-snug whitespace-pre-wrap border-l border-white/[0.06] pl-2">
          {source.fragment_text}
        </p>
      ) : null}
      {risHref ? (
        <a
          href={risHref}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[9px] text-emerald-300/80 hover:text-emerald-200"
        >
          <ExternalLink className="w-3 h-3" aria-hidden />
          Im RIS öffnen
        </a>
      ) : null}
    </div>
  );
}

function LexStructuredProof({ proof }: { proof: LexProof }) {
  const when = formatLexRetrievedAt(proof.retrieved_at);
  const sourceCount = proof.sources?.length ?? 0;

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-slate-300 leading-snug">{explainLexProof(proof)}</p>

      <dl className="grid gap-1 text-[9px] text-slate-500">
        {when ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Abgerufen</dt>
            <dd className="text-slate-400">{when}</dd>
          </div>
        ) : null}
        {proof.snapshot_version ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Snapshot</dt>
            <dd className="font-mono text-slate-400">{proof.snapshot_version}</dd>
          </div>
        ) : null}
        {proof.audit_id ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Audit-ID</dt>
            <dd className="font-mono text-slate-400 break-all">{proof.audit_id}</dd>
          </div>
        ) : null}
        {proof.lex_stamp ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Stempel</dt>
            <dd className="font-mono text-emerald-300/80 break-all">{proof.lex_stamp}</dd>
          </div>
        ) : null}
        {proof.lex_query ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Suchanfrage</dt>
            <dd className="text-slate-400 whitespace-pre-wrap">{proof.lex_query}</dd>
          </div>
        ) : null}
      </dl>

      {sourceCount > 0 ? (
        <div className="space-y-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300/80">
            Quellen ({sourceCount})
          </div>
          {proof.sources!.map((s) => (
            <LexProofSourceCard key={s.norm_id} source={s} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function JudicatureCaseCard({ item }: { item: JudicatureProofCase }) {
  const coreSentences = item.kernsaetze?.length ? item.kernsaetze : item.rechtssaetze;
  return (
    <div className="rounded border border-violet-500/20 bg-violet-500/[0.05] px-2 py-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <PortalBadge
          label={item.geschaeftszahl}
          accent="violet"
          className="!px-2 !py-0.5 !text-[9px] !font-mono"
        />
        <span className="text-[9px] font-mono text-violet-200/70">{item.case_id}</span>
        {item.date ? <span className="text-[9px] text-slate-500">· {item.date}</span> : null}
      </div>
      {item.leitsatz ? (
        <p className="text-[10px] font-medium text-slate-300 leading-snug">{item.leitsatz}</p>
      ) : null}
      {coreSentences?.length ? (
        <ul className="space-y-1 border-l border-violet-400/20 pl-2">
          {coreSentences.map((sentence, index) => (
            <li key={`${item.case_id}-core-${index}`} className="text-[10px] text-slate-400 leading-snug">
              „{sentence}“
            </li>
          ))}
        </ul>
      ) : null}
      {item.norm_refs?.length ? (
        <p className="text-[9px] text-slate-500">
          Normbezüge (nur Verweise): {item.norm_refs.join(', ')}
        </p>
      ) : null}
      <a
        href={item.source_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[9px] text-violet-300/85 hover:text-violet-200"
      >
        <ExternalLink className="w-3 h-3" aria-hidden />
        OGH-Quelle im RIS öffnen
      </a>
    </div>
  );
}

function JudicatureStructuredProof({ proof }: { proof: JudicatureProof }) {
  return (
    <div className="space-y-2">
      <dl className="grid gap-1 text-[9px] text-slate-500">
        <div className="flex gap-2">
          <dt className="shrink-0 text-slate-600">Stempel</dt>
          <dd className="font-mono text-violet-300/85 break-all">{proof.jud_stamp}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 text-slate-600">Audit-ID</dt>
          <dd className="font-mono text-slate-400 break-all">{proof.audit_id}</dd>
        </div>
        {proof.snapshot_version ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Snapshot</dt>
            <dd className="font-mono text-slate-400">{proof.snapshot_version}</dd>
          </div>
        ) : null}
      </dl>
      {proof.status === 'no_match' || proof.cases.length === 0 ? (
        <p className="rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1.5 text-[10px] text-slate-400">
          Kein belastbarer OGH-Treffer im aktuellen JUDICATURE-Snapshot.
        </p>
      ) : (
        <div className="space-y-1.5">
          {proof.cases.map((item) => (
            <JudicatureCaseCard key={item.case_id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function formatMemDate(iso: string): string {
  if (!iso) return '';
  const d = iso.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return iso;
}

interface ArkoResponseProofProps {
  text: string;
  /** Modul C: aus Recall — nicht im Fließtext (§7.5). */
  memProof?: MemProofItem[];
  /** Strukturierter LEXCORE-Nachweis aus n8n (bevorzugt gegenüber Text-Parsing). */
  lexProof?: LexProof | null;
  /** Strukturierter OGH-Nachweis; wird immer in einem eigenen JUD-Block gerendert. */
  judProof?: JudicatureProof | null;
}

/** Ausklappbar: MEM / LEX / MEM+LEX — Quellen, Warum, Wann (SKALIERUNG §7.5). */
export const ArkoResponseProof: React.FC<ArkoResponseProofProps> = ({
  text,
  memProof,
  lexProof,
  judProof,
}) => {
  const structuredProof = hasLexProofData(lexProof) ? lexProof! : null;
  const textQuotes = extractLexQuotesFromText(text);
  const mergedProof: LexProof | null = structuredProof
    ? {
        ...structuredProof,
        sources:
          structuredProof.sources && structuredProof.sources.length > 0
            ? structuredProof.sources
            : textQuotes,
      }
    : textQuotes.length > 0
      ? { sources: textQuotes }
      : null;
  const hasStructuredLex = hasLexProofData(mergedProof);
  const lexSources = hasStructuredLex
    ? []
    : extractArkoSources(text).filter(
        (item) => item.kind === 'lex_stamp' || item.kind === 'norm' || item.kind === 'ris',
      );
  const hasMem = Boolean(memProof && memProof.length > 0);
  const hasLex = hasStructuredLex || lexSources.length > 0;
  const structuredJud = hasJudicatureProofData(judProof) ? judProof! : null;
  const hasJud = structuredJud !== null;
  if (!hasMem && !hasLex && !hasJud) return null;

  const labelParts: string[] = [];
  if (hasMem) labelParts.push('MEM');
  if (hasLex) labelParts.push('LEX');
  if (hasJud) labelParts.push('JUD');

  const lexSummary = hasStructuredLex && mergedProof?.sources?.length
    ? ` · ${mergedProof.sources.length} Quelle${mergedProof.sources.length === 1 ? '' : 'n'}`
    : '';

  return (
    <details className="mt-1.5 max-w-[80%] group">
      <summary className="list-none cursor-pointer select-none flex items-center gap-1">
        <ChevronRight
          className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90"
          aria-hidden
        />
        <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold text-slate-400 hover:text-slate-200 hover:border-emerald-500/30 transition-colors">
          <BookMarked className="w-3 h-3" aria-hidden />
          Nachweis ({labelParts.join('+')}){lexSummary}
        </span>
      </summary>
      <div className="mt-2 pl-1 space-y-2 border-l border-white/[0.08] ml-1">
        {hasMem ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-blue-300/80">
              <Brain className="w-3 h-3" aria-hidden />
              Gedächtnis
            </div>
            {memProof!.map((item) => (
              <div
                key={item.vermerk_id}
                className="rounded border border-blue-500/15 bg-blue-500/[0.04] px-2 py-1.5"
              >
                <div className="text-[9px] font-mono text-blue-200/90">
                  {item.vermerk_id}
                  {item.gueltig_von ? ` · ${formatMemDate(item.gueltig_von)}` : ''}
                  {item.vertrauen ? ` · ${item.vertrauen}` : ''}
                </div>
                <p className="mt-0.5 text-[10px] text-slate-300 leading-snug whitespace-pre-wrap">
                  {item.inhalt}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {hasLex ? (
          <div className="space-y-1.5">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300/80">
              LEXCORE
            </div>
            {hasStructuredLex && mergedProof ? (
              <LexStructuredProof proof={mergedProof} />
            ) : (
              lexSources.map((item, i) => renderLexSourceItem(item, i))
            )}
          </div>
        ) : null}
        {hasJud && structuredJud ? (
          <div className="space-y-1.5 border-t border-white/[0.08] pt-2">
            <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-violet-300/85">
              <Scale className="w-3 h-3" aria-hidden />
              OGH-Judikatur
            </div>
            <JudicatureStructuredProof proof={structuredJud} />
          </div>
        ) : null}
      </div>
    </details>
  );
};

/** @deprecated Alias — nutze ArkoResponseProof */
export const ArkoBotSources: React.FC<ArkoResponseProofProps> = (props) => (
  <ArkoResponseProof {...props} />
);

/** Bot-Text: bei LEX nur Kurzzeile — Details im Ausklapp-Block. */
export function formatBotMessageForDisplay(
  text: string,
  lexProof?: LexProof | null,
  judProof?: JudicatureProof | null,
): string {
  const normIds = collectLexNormIds(text, lexProof);
  const lexFormatted = formatLexBubbleText(text, lexProof, normIds);
  return hasJudicatureProofData(judProof) || /\[JUD:/i.test(lexFormatted)
    ? stripJudicatureStampForDisplay(lexFormatted)
    : lexFormatted;
}

/** Legacy-Helfer: LEX/MEM-Nachweis vorhanden? */
export function hasArkoProof(
  text: string,
  memProof?: MemProofItem[],
  lexProof?: LexProof | null,
  judProof?: JudicatureProof | null,
): boolean {
  return (
    Boolean(memProof && memProof.length > 0) ||
    hasLexProofData(lexProof) ||
    hasJudicatureProofData(judProof) ||
    hasArkoLexSources(text)
  );
}
